import type {
  AmendmentRecord,
  ContractRecord,
  MembershipAmendmentDataType,
  PayPeriodRecordMap
} from '@libs/data-access/entities'
import type { FieldType } from '@spa/services/contractPayrollLock'
import { rangeIsValid } from '@spa/services/contractPayrollLock'
import momentJs from 'moment'
import type { DateRange } from 'moment-range'
import * as MomentRange from 'moment-range'

const moment = MomentRange.extendMoment(momentJs as any)

/**
 * Determines if the given range overlaps with any locked pay periods.
 *
 * @param startDate - The start date of the new range.
 * @param endDate - The end date of the new range.
 * @param lockedPayPeriodsRange - The range of locked pay periods to check against.
 * @returns `true` if the new range overlaps with any locked pay periods, `false` otherwise.
 */
export const rangeOverlaps = (
  startDate: string,
  endDate?: string | null,
  lockedPayPeriodsRange?: DateRange | null
) => {
  // Calculate the range of the new amendment.
  const newAmendmentRange = moment.range(
    moment.parseZone(startDate).startOf('day'),
    endDate ? moment.parseZone(endDate).endOf('day') : null
  )

  // Check if the amendment is new and doesn't overlap with any locked pay periods.
  return lockedPayPeriodsRange
    ? newAmendmentRange.overlaps(lockedPayPeriodsRange)
    : false
}

/**
 * Checks if the given contract time is equal to the contract's contract time.
 *
 * @param contract - The contract record to check against.
 * @param contract_time - The contract time to check.
 * @returns `true` if the contract time is equal to the contract's contract time, or if the contract time is `undefined`, `false` otherwise.
 */
export const isContractTimeEqualToContract = (
  contract: ContractRecord | undefined,
  contract_time: number | undefined
) => !contract_time || contract?.contract_time === contract_time

/**
 * Checks if the given contract time is equal to the amendment's contract time.
 *
 * @param amendment - The amendment record to check against.
 * @param contract_time - The contract time to check.
 * @returns `true` if the contract time is equal to the amendment's contract time, or if the contract time is `undefined`, `false` otherwise.
 */
export const isContractTimeEqualToAmendment = (
  amendment: AmendmentRecord | undefined,
  contract_time: number | undefined
) => amendment?.contract_time === contract_time

/**
 * Checks if the given amendment range is valid and doesn't overlap with any locked pay periods.
 *
 * @param amendment - The amendment record to check against.
 * @param startDate - The start date of the new amendment range.
 * @param endDate - The end date of the new amendment range, if any.
 * @param fieldType - The type of field being checked ('startDate', 'endDate', or `null`).
 * @param lockedPayPeriods - The locked pay periods to check against.
 * @returns `true` if the amendment range is valid and doesn't overlap with any locked pay periods, `false` otherwise.
 */
export const isAmendmentRangeValid = (
  amendment: MembershipAmendmentDataType,
  startDate: string,
  endDate: string | null | undefined,
  fieldType: FieldType,
  lockedPayPeriods: PayPeriodRecordMap | undefined
) =>
  rangeIsValid(
    startDate,
    amendment.start_date,
    amendment.end_date,
    startDate,
    endDate,
    lockedPayPeriods,
    fieldType
  )
